import React, {useRef} from 'react'
import ErrorToast from '@/components/ErrorToast'
import useToast from '@/hooks/useToast'
import {useEffect, useState} from 'react'
import {useQuery} from '@/hooks/useApi'

const useSelect = ({
  api,
  queryKey,
  optionKey,
  searchKey,
  optionText,
  onMetaData,
  queryParams,
  apiParams,
  staleTime = 3600,
  shouldShowToast = true,
  retry = 5,
}) => {
  const {toast} = useToast()
  const limit = 20
  const [offset, setOffset] = useState(0)
  const [options, setOptions] = useState([])
  const [totalPageCount, setTotalPageCount] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')
  const [keyword, setKeyword] = useState('')
  const timeoutRef = useRef(null)
  const infiniteScroll = true
  const loadMorePercentage = 0.1
  const {
    data,
    isFetching: isLoadingMoreData,
    error,
    status,
  } = useQuery({
    queryKey: [queryKey, limit, offset, queryParams],
    queryFn: async () => {
      const requestParams = {page: offset, count: limit, ...queryParams}

      const response = apiParams ? await api(...Object.values(apiParams), requestParams) : await api(requestParams)

      if (response.status >= 400) {
        throw response.error
      }

      return response.data
    },
    staleTime,
    retry,
  })

  useEffect(() => {
    if (error && shouldShowToast) {
      toast.custom(t => <ErrorToast t={t} error={error} />)
    }
  }, [error])

  useEffect(() => {
    if (data?.data && !error) {
      const opt =
        data?.data?.map(item => {
          const myObject = {
            key: item[optionKey],
            text: item[optionText],
          }

          if (onMetaData) {
            myObject.metadata = onMetaData(item)
          }
          return myObject
        }) || []
      if (offset == 0) {
        setOptions([...opt])
      } else {
        setOptions([...options, ...opt])
      }
      if (data?.meta.totalCount > 0) {
        setTotalPageCount(data?.meta?.totalCount)
      }
    }
  }, [data])

  const onLoadMoreData = () => {
    if (options.length === totalPageCount) {
      return
    }
    if (!isLoadingMoreData) {
      setOffset(offset => offset + 1)
    }
  }

  const debounce = (search, delay) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    timeoutRef.current = setTimeout(() => {
      handleSearch(search)
    }, delay)
  }

  const handleSearch = query => {
    setOffset(0)
    setSearchQuery(query)
  }

  const debouncedSearch = search => debounce(search, 500)

  const onSearchKeywordChange = e => {
    debouncedSearch(e.target.value)
    setKeyword(e.target.value)
  }

  return {
    options,
    isLoadingMoreData,
    onLoadMoreData,
    infiniteScroll,
    loadMorePercentage,
    onSearchKeywordChange,
    keyword,
    error,
    status,
    searchQuery,
  }
}

export default useSelect
